import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { Role } from '@b4l/shared'
import { ROLES } from '@b4l/shared'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { PasswordField } from '../../components/ui/PasswordField'
import { semnaturaDinPoza } from '../../lib/semnaturaDinPoza'

interface UserRow {
  id: number
  email: string
  name: string
  role: Role
  active: boolean
  mustChangePassword: boolean
  locked: boolean
  agentName: string | null
  /** Ținta lunară de bucăți. `null` = cea comună a firmei. */
  targetQtyLunar: number | null
  /** Funcția afișată în interfață. Rolul rămâne cel care dă drepturile. */
  jobTitle: string | null
  /** Câte task-uri active consideră că poate duce în paralel. Reper pentru „încărcare". */
  taskCapacity: number
  /** Gestiunea proprie din ExpertAccounts — stocul purtat pe teren. */
  eaLocId: number | null
  /** Semnătura olografă, PNG ca `data:` URI. Apare pe procesele-verbale unde e numele lui. */
  semnatura: string | null
  lastSeenAt: string | null
  createdAt: string
}

const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  administrativ: 'Operațional',
  marketing: 'Marketing',
  depozit: 'Depozit',
  contabilitate: 'Contabilitate',
  vanzari: 'Vânzări',
  manager_vanzari: 'Manager vânzări',
}

const CreateSchema = z.object({
  email: z.string().email('Email invalid').toLowerCase(),
  name: z.string().min(1, 'Numele este obligatoriu').max(120),
  role: z.enum(ROLES),
  password: z
    .string()
    .min(8, 'Min 8 caractere')
    .refine((v) => /[A-Z]/.test(v), 'O literă mare obligatorie')
    .refine((v) => /[a-z]/.test(v), 'O literă mică obligatorie')
    .refine((v) => /\d/.test(v), 'O cifră obligatorie'),
})

type CreateInput = z.infer<typeof CreateSchema>

/** „azi", „acum 3 zile", „niciodată" — cum se citește, nu o dată pe care s-o scazi în cap. */
function deCand(iso: string | null): string {
  if (!iso) return 'niciodată'
  const zile = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (zile <= 0) return 'azi'
  if (zile === 1) return 'ieri'
  if (zile < 30) return `acum ${zile} zile`
  const luni = Math.floor(zile / 30)
  return luni === 1 ? 'acum o lună' : `acum ${luni} luni`
}

/** Peste o săptămână fără logare, roșu. Niciodată — tot roșu: contul n-a fost folosit vreodată. */
function culoareLogare(iso: string | null): string {
  if (!iso) return 'text-b4l-red-dark'
  const zile = (Date.now() - new Date(iso).getTime()) / 86_400_000
  if (zile > 7) return 'text-b4l-red-dark'
  if (zile > 2) return 'text-b4l-amber-dark'
  return 'text-b4l-text-muted'
}

export function SetariUtilizatoriPage() {
  const queryClient = useQueryClient()
  const me = useAuthStore((s) => s.user)
  /*
    FILTRELE. Tabelul a trecut de douazeci de randuri si doisprezece coloane; „cine e pe depozit"
    sau „cati au functia Vanzari" se raspundea cu degetul pe ecran (Alexandru, 01.10.2026).

    Se filtreaza pe ce se vede, nu pe server: lista e mica si vine intreaga oricum, iar un filtru
    pe server ar fi adaugat o cerere pentru fiecare apasare, fara niciun castig.
  */
  const [filtruRol, setFiltruRol] = useState<'' | Role>('')
  const [filtruFunctie, setFiltruFunctie] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [resetTarget, setResetTarget] = useState<UserRow | null>(null)
  const [resetResult, setResetResult] = useState<{
    tempPassword: string
    emailed: boolean
    emailError?: string
  } | null>(null)

  const usersQuery = useQuery<{ users: UserRow[] }>({
    queryKey: ['settings', 'users'],
    queryFn: async () => (await api.get<{ users: UserRow[] }>('/settings/users')).data,
  })

  const toti = usersQuery.data?.users ?? []
  /* Functiile existente, fara dubluri si fara goale. Vin din date; vezi comentariul filtrelor. */
  const functii = [...new Set(toti.map((u) => u.jobTitle?.trim()).filter(Boolean))].sort((a, b) =>
    a!.localeCompare(b!, 'ro'),
  ) as string[]
  const randuri = toti.filter(
    (u) =>
      (!filtruRol || u.role === filtruRol) &&
      (!filtruFunctie || (u.jobTitle?.trim() ?? '') === filtruFunctie),
  )

  const createMutation = useMutation({
    mutationFn: async (data: CreateInput) => (await api.post('/settings/users', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'users'] })
      toast.success('Utilizator creat')
      setCreateOpen(false)
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la creare'
      toast.error(message)
    },
  })

  const updateRoleMutation = useMutation({
    mutationFn: async ({ id, role }: { id: number; role: Role }) =>
      (await api.patch(`/settings/users/${id}`, { role })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'users'] })
      toast.success('Rol actualizat')
    },
  })

  const agentNamesQuery = useQuery<{ agentNames: string[] }>({
    queryKey: ['settings', 'agent-names'],
    queryFn: async () =>
      (await api.get<{ agentNames: string[] }>('/settings/users/agent-names')).data,
  })

  const updateJobTitleMutation = useMutation({
    mutationFn: async ({ id, jobTitle }: { id: number; jobTitle: string | null }) =>
      (await api.patch(`/settings/users/${id}`, { jobTitle })).data,
    onSuccess: () => {
      toast.success('Funcție actualizată.')
      void queryClient.invalidateQueries({ queryKey: ['settings-users'] })
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare'
      toast.error(message)
    },
  })

  /**
   * Gestiunea proprie din ExpertAccounts.
   *
   * Victor: „ei au nevoie de un stock al lor pe care sa il poarte in masina". Transferurile
   * intra acolo, iar bonurile lui de consum scad de acolo — deci lipsurile au un proprietar.
   */
  const updateGestiuneMutation = useMutation({
    mutationFn: async ({ id, eaLocId }: { id: number; eaLocId: number | null }) =>
      (await api.patch(`/settings/users/${id}`, { eaLocId })).data,
    onSuccess: () => {
      toast.success('Gestiune actualizată')
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: () => toast.error('Nu am putut salva gestiunea'),
  })

  const updateCapacityMutation = useMutation({
    mutationFn: async ({ id, taskCapacity }: { id: number; taskCapacity: number }) =>
      (await api.patch(`/settings/users/${id}`, { taskCapacity })).data,
    onSuccess: () => {
      toast.success('Capacitate actualizată.')
      void queryClient.invalidateQueries({ queryKey: ['settings-users'] })
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare'
      toast.error(message)
    },
  })

  /*
    Semnatura, incarcata ca poza si curatata in browser.

    Alexandru: „adauga semnatura lui Victor pe toate formularele pe care apare numele lui". E pe
    UTILIZATOR, nu pe actiune: acelasi om semneaza la mai multe actiuni, iar numele lui vine deja
    de aici.
  */
  const updateSemnaturaMutation = useMutation({
    mutationFn: async ({ id, semnatura }: { id: number; semnatura: string | null }) =>
      (await api.patch(`/settings/users/${id}`, { semnatura })).data,
    onSuccess: (_d, v) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'users'] })
      toast.success(v.semnatura ? 'Semnătură salvată' : 'Semnătură ștearsă')
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare'
      toast.error(message)
    },
  })

  /*
    Tinta lunara de bucati, pentru fila „Cantitati" din tabloul de vanzari.

    Gol inseamna tinta comuna a firmei — azi 1000 buc/luna, scrisa in cod. Campul e pentru
    exceptii: daca s-ar completa la toata lumea, schimbarea cifrei ar cere un tur prin toti
    utilizatorii, in loc de o linie.
  */
  const updateTintaMutation = useMutation({
    mutationFn: async ({ id, targetQtyLunar }: { id: number; targetQtyLunar: number | null }) =>
      (await api.patch(`/settings/users/${id}`, { targetQtyLunar })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'users'] })
      void queryClient.invalidateQueries({ queryKey: ['settings-users'] })
      toast.success('Țintă actualizată')
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: { message?: string } } } }
      toast.error(e.response?.data?.error?.message ?? 'Nu s-a putut salva ținta')
    },
  })

  const updateAgentMutation = useMutation({
    mutationFn: async ({ id, agentName }: { id: number; agentName: string | null }) =>
      (await api.patch(`/settings/users/${id}`, { agentName })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'users'] })
      toast.success('Agent actualizat')
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare'
      toast.error(message)
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) =>
      (await api.patch(`/settings/users/${id}`, { active })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'users'] })
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare'
      toast.error(message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/settings/users/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'users'] })
      toast.success('Utilizator șters')
    },
  })

  const resetMutation = useMutation({
    mutationFn: async (id: number) =>
      (
        await api.post<{ tempPassword: string; emailed: boolean; emailError?: string }>(
          `/settings/users/${id}/reset-password`,
          {},
        )
      ).data,
    onSuccess: (data) => {
      setResetResult(data)
      queryClient.invalidateQueries({ queryKey: ['settings', 'users'] })
    },
  })

  const createForm = useForm<CreateInput>({
    resolver: zodResolver(CreateSchema),
    defaultValues: { email: '', name: '', role: 'marketing', password: '' },
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Utilizatori</h1>
        <Button
          onClick={() => {
            createForm.reset({ email: '', name: '', role: 'marketing', password: '' })
            setCreateOpen(true)
          }}
        >
          + Adaugă utilizator
        </Button>
      </div>

      {/*
        Functiile vin din DATE, nu dintr-o lista fixa: campul e text liber, iar o lista scrisa de
        noi ar ramane in urma la prima functie noua — si nimeni n-ar sti de ce lipseste cineva.
      */}
      <Card className="mb-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Rol
            <select
              value={filtruRol}
              onChange={(e) => setFiltruRol(e.target.value as '' | Role)}
              className="rounded border border-b4l-border px-2 py-1 text-sm"
            >
              <option value="">Toate rolurile</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Funcție
            <select
              value={filtruFunctie}
              onChange={(e) => setFiltruFunctie(e.target.value)}
              className="rounded border border-b4l-border px-2 py-1 text-sm"
            >
              <option value="">Toate funcțiile</option>
              {functii.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
          {(filtruRol || filtruFunctie) && (
            <button
              onClick={() => {
                setFiltruRol('')
                setFiltruFunctie('')
              }}
              className="pb-1 text-xs text-b4l-blue hover:underline"
            >
              Arată toți
            </button>
          )}
          <span className="pb-1 text-xs text-b4l-text-muted">
            {randuri.length === (usersQuery.data?.users.length ?? 0)
              ? `${randuri.length} utilizatori`
              : `${randuri.length} din ${usersQuery.data?.users.length ?? 0}`}
          </span>
        </div>
      </Card>

      <Card>
        {usersQuery.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : (
          /*
            COLOANA DE NUME ramane pe loc la derulare laterala.

            Tabelul are douasprezece coloane si trece de latimea ecranului; pana la „Actiuni",
            numele a iesit demult din cadru, si nu mai stii al cui e randul pe care apesi „Sterge"
            (Alexandru, 01.10.2026). Un buton distructiv apasat pe randul gresit nu se mai
            intoarce.

            `sticky` pe celula, nu pe coloana: HTML-ul n-are coloane ca obiecte. Fundalul trebuie
            pus explicit, altfel randurile de dedesubt se vad prin ea cand aluneca.
          */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="sticky left-0 z-10 bg-white py-2 pr-3 font-medium">Nume</th>
                  <th className="py-2 pr-3 font-medium">Email</th>
                  <th className="py-2 pr-3 font-medium">Rol</th>
                  <th className="py-2 pr-3 font-medium">Funcție afișată</th>
                  <th className="py-2 pr-3 font-medium">Capacitate</th>
                  <th className="py-2 pr-3 font-medium">Agent</th>
                  {/* „Gol = 1000, 0 = fără țintă" nu încape în antet; stă în `title`-ul câmpului. */}
                  <th
                    className="py-2 pr-3 font-medium"
                    title="Gol = ținta firmei (1000 buc/lună). 0 = fără țintă."
                  >
                    Țintă buc/lună
                  </th>
                  <th className="py-2 pr-3 font-medium">Gestiune EA</th>
                  <th className="py-2 pr-3 font-medium">Semnătură</th>
                  <th className="py-2 pr-3 font-medium">Ultima activitate</th>
                  <th className="py-2 pr-3 font-medium">Stare</th>
                  <th className="py-2 pr-3 font-medium">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {randuri.map((u) => {
                  const isMe = me?.id === u.id
                  return (
                    <tr key={u.id} className="border-b border-b4l-border last:border-0">
                      <td className="sticky left-0 z-10 whitespace-nowrap bg-white py-2 pr-3">
                        {u.name}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">{u.email}</td>
                      <td className="py-2 pr-3">
                        <select
                          value={u.role}
                          onChange={(e) =>
                            updateRoleMutation.mutate({ id: u.id, role: e.target.value as Role })
                          }
                          disabled={isMe}
                          className="rounded border border-b4l-border bg-white px-2 py-1 text-xs"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABELS[r]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 pr-3">
                        {/* Ce citește omul despre el. Rolul de alături dă drepturile și rămâne
                          un termen de sistem — „admin" nu e o meserie. */}
                        <input
                          defaultValue={u.jobTitle ?? ''}
                          placeholder={ROLE_LABELS[u.role]}
                          onBlur={(e) => {
                            const v = e.target.value.trim()
                            if (v !== (u.jobTitle ?? '')) {
                              updateJobTitleMutation.mutate({ id: u.id, jobTitle: v || null })
                            }
                          }}
                          className="w-32 rounded border border-b4l-border bg-white px-2 py-1 text-xs"
                          title="Ce se afișează în interfață. Nu schimbă drepturile."
                        />
                      </td>
                      <td className="py-2 pr-3">
                        {/* Reperul pentru „încărcare" din tabloul echipei. Declarat de om, nu
                          ghicit de sistem — altfel procentul arată precis fără să fie. */}
                        <input
                          type="number"
                          min={1}
                          max={100}
                          defaultValue={u.taskCapacity}
                          onBlur={(e) => {
                            const v = Number(e.target.value)
                            if (v >= 1 && v <= 100 && v !== u.taskCapacity) {
                              updateCapacityMutation.mutate({ id: u.id, taskCapacity: v })
                            }
                          }}
                          className="w-16 rounded border border-b4l-border bg-white px-2 py-1 text-xs"
                          title="Câte task-uri active poate duce în paralel"
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <select
                          value={u.agentName ?? ''}
                          onChange={(e) =>
                            updateAgentMutation.mutate({
                              id: u.id,
                              agentName: e.target.value || null,
                            })
                          }
                          className="rounded border border-b4l-border bg-white px-2 py-1 text-xs"
                          title="Leagă userul de un agent din portofolii (pagina Clienții mei)"
                        >
                          <option value="">— fără —</option>
                          {u.agentName &&
                            !(agentNamesQuery.data?.agentNames ?? []).includes(u.agentName) && (
                              <option value={u.agentName}>{u.agentName}</option>
                            )}
                          {(agentNamesQuery.data?.agentNames ?? []).map((a) => (
                            <option key={a} value={a}>
                              {a}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 pr-3">
                        {/*
                        Se arată doar la cine are portofoliu: la un om de marketing sau din
                        depozit, o țintă de vânzări n-ar însemna nimic și ar invita la completat
                        degeaba.
                      */}
                        {u.agentName ? (
                          <input
                            type="number"
                            min={0}
                            defaultValue={u.targetQtyLunar ?? ''}
                            placeholder="1000"
                            onBlur={(e) => {
                              const brut = e.target.value.trim()
                              const v = brut === '' ? null : Number(brut)
                              if (v !== null && (!Number.isFinite(v) || v < 0)) return
                              if (v !== (u.targetQtyLunar ?? null)) {
                                updateTintaMutation.mutate({ id: u.id, targetQtyLunar: v })
                              }
                            }}
                            className="w-24 rounded border border-b4l-border bg-white px-2 py-1 text-right text-xs tabular-nums"
                            title="Gol = ținta comună a firmei (1000 buc/lună). 0 = fără țintă, nu se mai calculează procent."
                          />
                        ) : (
                          <span className="text-xs text-b4l-text-dim">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        {/*
                        Gestiunea proprie din EA — stocul pe care agentul îl poartă în mașină.
                        Gol = transferurile și bonurile lui merg în gestiunea comună a
                        modulului. Nu toți oamenii merg pe teren.
                      */}
                        <input
                          type="number"
                          min={1}
                          defaultValue={u.eaLocId ?? ''}
                          placeholder="—"
                          onBlur={(e) => {
                            const brut = e.target.value.trim()
                            const v = brut === '' ? null : Number(brut)
                            if (v !== null && (!Number.isInteger(v) || v < 1)) return
                            if (v !== (u.eaLocId ?? null)) {
                              updateGestiuneMutation.mutate({ id: u.id, eaLocId: v })
                            }
                          }}
                          className="w-20 rounded border border-b4l-border bg-white px-2 py-1 text-xs"
                          title="locid-ul gestiunii lui din ExpertAccounts. Gol = gestiunea comună de sampling."
                        />
                      </td>
                      <td className="py-2 pr-3">
                        {/*
                        Semnatura, pusa pe procesele-verbale unde apare numele omului — rubrica
                        „Am predat", pana acum goala.

                        Se incarca o poza a semnaturii facute pe hartie; browserul o decupeaza si
                        ii scoate fondul inainte s-o trimita. Fotografia intreaga nu pleaca de pe
                        calculatorul de aici: in baza ajunge strict semnatura.
                      */}
                        <div className="flex items-center gap-2">
                          {u.semnatura ? (
                            <img
                              src={u.semnatura}
                              alt={`Semnătura ${u.name}`}
                              className="h-8 w-auto max-w-[80px] object-contain"
                            />
                          ) : (
                            <span className="text-xs text-b4l-text-dim">—</span>
                          )}
                          <label className="cursor-pointer text-xs text-b4l-blue underline">
                            {u.semnatura ? 'schimbă' : 'încarcă'}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const f = e.target.files?.[0]
                                e.target.value = ''
                                if (!f) return
                                semnaturaDinPoza(f)
                                  .then((png) =>
                                    updateSemnaturaMutation.mutate({ id: u.id, semnatura: png }),
                                  )
                                  .catch((err: Error) => toast.error(err.message))
                              }}
                            />
                          </label>
                          {u.semnatura && (
                            <button
                              onClick={() =>
                                updateSemnaturaMutation.mutate({ id: u.id, semnatura: null })
                              }
                              className="text-xs text-b4l-red underline"
                            >
                              șterge
                            </button>
                          )}
                        </div>
                      </td>
                      {/*
                      Ultima activitate — cerut de Victor: „pt a vedea activitatea lor […] că mi
                      se pare că nu prea dau prin platformă".

                      NU ultima logare: sesiunea ține zile întregi, deci cineva care intră zilnic
                      și nu se deloghează niciodată ar fi arătat „niciodată". Exact oamenii
                      activi ar fi ieșit cei mai inactivi.

                      Se colorează după cât e de veche: peste o săptămână, roșu. Nu ca reproș —
                      ca semnal că cineva lucrează pe lângă CRM, și merită întrebat de ce.
                    */}
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <span className={`text-xs ${culoareLogare(u.lastSeenAt)}`}>
                          {deCand(u.lastSeenAt)}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex gap-1">
                          {u.active ? (
                            <span className="rounded bg-b4l-green-light px-2 py-0.5 text-[10px] text-b4l-green-dark">
                              activ
                            </span>
                          ) : (
                            <span className="rounded bg-b4l-bg px-2 py-0.5 text-[10px] text-b4l-text-muted">
                              dezactivat
                            </span>
                          )}
                          {u.locked && (
                            <span className="rounded bg-b4l-red-light px-2 py-0.5 text-[10px] text-b4l-red-dark">
                              blocat
                            </span>
                          )}
                          {u.mustChangePassword && (
                            <span className="rounded bg-b4l-amber-light px-2 py-0.5 text-[10px] text-b4l-amber-dark">
                              schimbare parolă
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex gap-1">
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setResetTarget(u)
                              setResetResult(null)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Resetează parola
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() =>
                              toggleActiveMutation.mutate({ id: u.id, active: !u.active })
                            }
                            disabled={isMe && u.active}
                            className="!px-2 !py-1 !text-xs"
                          >
                            {u.active ? 'Dezactivează' : 'Activează'}
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Ștergi utilizatorul ${u.email}?`)) {
                                deleteMutation.mutate(u.id)
                              }
                            }}
                            disabled={isMe}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Adaugă utilizator nou"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Anulează
            </Button>
            <Button
              onClick={() => createForm.handleSubmit((data) => createMutation.mutate(data))()}
              loading={createMutation.isPending}
            >
              Creează
            </Button>
          </>
        }
      >
        <form className="space-y-3" noValidate onSubmit={(e) => e.preventDefault()}>
          <Field
            label="Email"
            type="email"
            {...createForm.register('email')}
            error={createForm.formState.errors.email?.message}
          />
          <Field
            label="Nume complet"
            {...createForm.register('name')}
            error={createForm.formState.errors.name?.message}
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Rol</label>
            <select
              {...createForm.register('role')}
              className="w-full rounded border border-b4l-border px-3 py-2"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </div>
          <PasswordField
            label="Parola inițială"
            defaultVisible
            autoComplete="new-password"
            hint="Utilizatorul va fi obligat să o schimbe la prima autentificare."
            {...createForm.register('password')}
            error={createForm.formState.errors.password?.message}
          />
        </form>
      </Modal>

      <Modal
        open={!!resetTarget}
        onClose={() => {
          setResetTarget(null)
          setResetResult(null)
        }}
        title={`Resetează parola pentru ${resetTarget?.name ?? ''}`}
        footer={
          resetResult ? (
            <Button
              onClick={() => {
                setResetTarget(null)
                setResetResult(null)
              }}
            >
              OK
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setResetTarget(null)}>
                Anulează
              </Button>
              <Button
                variant="danger"
                onClick={() => resetTarget && resetMutation.mutate(resetTarget.id)}
                loading={resetMutation.isPending}
              >
                Generează parolă nouă
              </Button>
            </>
          )
        }
      >
        {!resetResult ? (
          <p className="text-sm text-b4l-text-muted">
            Se generează o parolă temporară pentru <strong>{resetTarget?.email}</strong>. La
            următoarea autentificare, utilizatorul trebuie să o schimbe.
          </p>
        ) : (
          <div className="space-y-2">
            {resetResult.emailed ? (
              <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                ✓ Parola temporară a fost trimisă automat pe email către{' '}
                <strong>{resetTarget?.email}</strong>. Utilizatorul o schimbă la prima
                autentificare.
              </div>
            ) : (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Emailul nu a putut fi trimis
                {resetResult.emailError ? ` (${resetResult.emailError})` : ''}. Transmite parola
                manual, pe un canal sigur.
              </div>
            )}
            <p className="text-sm text-b4l-text-muted">Parola temporară generată:</p>
            <div className="rounded border border-b4l-border bg-b4l-bg-soft p-3 font-mono text-sm">
              {resetResult.tempPassword}
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
